import React, { useEffect, useState } from "react"
import { MdCheck, MdOutlineClose } from "react-icons/md"
import styles from "./markQuotationStatusConfirmPopup.module.scss"

export type MarkQuotationStatusConfirmVariant = "confirmed" | "lost"

type Props = {
    isOpen: boolean
    variant: MarkQuotationStatusConfirmVariant
    staCode: string
    isSubmitting?: boolean
    onCancel: () => void
    onConfirm: (remark?: string) => void
}

const MarkQuotationStatusConfirmPopup: React.FC<Props> = ({
    isOpen,
    variant,
    staCode,
    isSubmitting = false,
    onCancel,
    onConfirm,
}) => {
    const [reasonForLoss, setReasonForLoss] = useState("")

    useEffect(() => {
        if (!isOpen) {
            setReasonForLoss("")
        }
    }, [isOpen, variant])

    useEffect(() => {
        if (!isOpen) return

        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === "Escape" && !isSubmitting) {
                onCancel()
            }
        }

        document.addEventListener("keydown", handleEscape)
        document.body.style.overflow = "hidden"

        return () => {
            document.removeEventListener("keydown", handleEscape)
            document.body.style.overflow = "unset"
        }
    }, [isOpen, isSubmitting, onCancel])

    if (!isOpen) return null

    const displayStaCode = String(staCode || "").trim() || "this quotation"
    const isConfirmed = variant === "confirmed"

    const handleOverlayClick = (event: React.MouseEvent<HTMLDivElement>) => {
        if (event.target === event.currentTarget && !isSubmitting) {
            onCancel()
        }
    }

    const handleConfirm = () => {
        if (isConfirmed) {
            onConfirm()
            return
        }
        onConfirm(reasonForLoss.trim())
    }

    return (
        <div className={styles.overlay} onClick={handleOverlayClick}>
            <div
                className={styles.dialog}
                role="dialog"
                aria-modal="true"
                aria-labelledby="mark-quotation-status-title"
                onClick={(event) => event.stopPropagation()}
            >
                <div
                    className={[
                        styles.iconCircle,
                        isConfirmed ? styles.iconCircleConfirmed : styles.iconCircleLost,
                    ].join(" ")}
                >
                    {isConfirmed ? (
                        <MdCheck size={28} color="#166534" />
                    ) : (
                        <MdOutlineClose size={28} color="#991B1B" />
                    )}
                </div>

                <h2 id="mark-quotation-status-title" className={styles.title}>
                    {isConfirmed ? "Confirm Quotation" : "Mark as Lost"}
                </h2>

                <p className={styles.message}>
                    Are you sure you want to mark <strong>{displayStaCode}</strong> as{" "}
                    {isConfirmed ? "Confirmed" : "Lost"}?
                    {isConfirmed
                        ? " This will move the quotation to the confirmed pipeline."
                        : " This action can be reversed later from the quotation details."}
                </p>

                {!isConfirmed && (
                    <div className={styles.field}>
                        <label className={styles.fieldLabel} htmlFor="reason-for-loss">
                            Reason for Loss (optional)
                        </label>
                        <input
                            id="reason-for-loss"
                            type="text"
                            className={styles.fieldInput}
                            value={reasonForLoss}
                            onChange={(event) => setReasonForLoss(event.target.value)}
                            placeholder="e.g. Client chose competitor, Budget constraints..."
                            disabled={isSubmitting}
                        />
                    </div>
                )}

                <div className={styles.actions}>
                    <button
                        type="button"
                        className={styles.cancelButton}
                        onClick={onCancel}
                        disabled={isSubmitting}
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        className={[
                            styles.confirmButton,
                            isConfirmed ? styles.confirmButtonSuccess : styles.confirmButtonDanger,
                        ].join(" ")}
                        onClick={handleConfirm}
                        disabled={isSubmitting}
                    >
                        {isConfirmed ? "Yes, Confirm" : "Mark as Lost"}
                    </button>
                </div>
            </div>
        </div>
    )
}

export default MarkQuotationStatusConfirmPopup
